import { Content } from '.generated/Feature.CW.model';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { tv } from 'tailwind-variants';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { withStandardComponentWrapper } from 'helpers/HOC';
import classNames from 'classnames';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';

export type StatisticsProps = Content.Statistics_Component & {
  isSidenavPart?: boolean;
};

export const StatisticsValue = (props: StatisticsProps): JSX.Element => {
  const statisticBlockList = props?.fields?.statisticBlockList;
  const totalLength = statisticBlockList?.length;
  const {
    container,
    optionalHeadingClass,
    statisticBlockWrapper,
    statisticBlockListClass,
    statisticBlockItemClass,
    contentWrapper,
    numberClass,
    labelClass,
    supportingTextClass,
  } = TailwindVariables();

  return (
    <ComponentWrapper isSidenavPart={props?.isSidenavPart}>
      <div className={container()}>
        <div
          className={classNames(
            optionalHeadingClass(),
            props?.isSidenavPart
              ? 'text-24 leading-28 lg:text-26 lg:leading-31'
              : 'text-26 leading-31 lg:text-36 lg:leading-43'
          )}
        >
          <PlainTextWrapper
            field={props?.fields?.optionalHeading}
            tag={props?.isSidenavPart ? 'h3' : 'h2'}
          />
        </div>

        <div className={statisticBlockWrapper()}>
          <ul
            className={`${statisticBlockListClass()} ${totalLength && totalLength === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}
          >
            {statisticBlockList?.map((listItem: Content.StatisticBlock, index) => {
              return (
                <li
                  key={index}
                  className={`${statisticBlockItemClass()} ${totalLength && totalLength === 3 ? 'lg:[&:nth-child(3n)]:border-r-0' : 'lg:[&:nth-child(4n)]:border-r-0'}`}
                >
                  <div className={contentWrapper()}>
                    <div className={numberClass()}>
                      <PlainTextWrapper field={listItem?.fields?.number} />
                    </div>
                    <div className={labelClass()}>
                      <PlainTextWrapper field={listItem?.fields?.statisticLabel} />
                    </div>
                    <div className={supportingTextClass()}>
                      <RichTextWrapper field={listItem?.fields?.supportingText} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </ComponentWrapper>
  );
};

export const StatisticsMedia = (props: StatisticsProps): JSX.Element => {
  const statisticBlockList = props?.fields?.statisticBlockList;
  const totalLength = statisticBlockList?.length;
  const {
    container,
    optionalHeadingClass,
    statisticBlockWrapper,
    statisticBlockListClass,
    statisticBlockItemClass,
    contentWrapper,
    labelClass,
    supportingTextClass,
    imageWrapper,
  } = TailwindVariables();

  return (
    <ComponentWrapper isSidenavPart={props?.isSidenavPart}>
      <div className={container()}>
        <div
          className={classNames(
            optionalHeadingClass(),
            props?.isSidenavPart
              ? 'text-24 leading-28 lg:text-26 lg:leading-31'
              : 'text-26 leading-31 lg:text-36 lg:leading-43'
          )}
        >
          <PlainTextWrapper
            field={props?.fields?.optionalHeading}
            tag={props?.isSidenavPart ? 'h3' : 'h2'}
          />
        </div>

        <div className={statisticBlockWrapper()}>
          <ul
            className={`${statisticBlockListClass()} ${totalLength && totalLength === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}
          >
            {statisticBlockList?.map((listItem: Content.StatisticBlock, index) => {
              return (
                <>
                  {listItem?.fields?.statisticImage?.value?.src && (
                    <li
                      key={index}
                      className={`${statisticBlockItemClass()} ${totalLength && totalLength === 3 ? 'lg:[&:nth-child(3n)]:border-r-0' : ' lg:[&:nth-child(4n)]:border-r-0'}`}
                    >
                      <div className={contentWrapper()}>
                        <div className={imageWrapper()}>
                          <div className="w-[125px] h-[125px]">
                            <ImageWrapper
                              field={listItem?.fields?.statisticImage}
                              className="w-[125px] h-[125px] object-cover rounded-sm"
                            />
                          </div>
                        </div>

                        <div className={labelClass()}>
                          <PlainTextWrapper field={listItem?.fields?.statisticLabel} />
                        </div>
                        <div className={supportingTextClass()}>
                          <RichTextWrapper field={listItem?.fields?.supportingText} />
                        </div>
                      </div>
                    </li>
                  )}
                </>
              );
            })}
          </ul>
        </div>
      </div>
    </ComponentWrapper>
  );
};

const Statistics = (props: StatisticsProps): JSX.Element => {
  return <StatisticsValue {...props} />; //Default variant
};

export const Default = withStandardComponentWrapper(Statistics);

export const TailwindVariables = tv({
  slots: {
    container: 'w-full max-w-screen-xxl mx-auto px-10 lg:px-20 lg:py-[80px] py-[48px]',
    optionalHeadingClass: 'text-gray-900 font-centuryGothic font-bold lg:pb-12 pb-8 ',
    statisticBlockWrapper: 'bg-blue-50 rounded-md',
    statisticBlockListClass: 'lg:grid gap-y-[24px] lg:py-12 px-16 lg:px-0',
    statisticBlockItemClass:
      'text-left flex lg:border-r-2 border-b-2 lg:border-b-0 border-blue-100 last:border-r-0 last:border-b-0 lg:px-12 py-16 lg:py-0',
    contentWrapper: 'flex flex-col w-full',
    numberClass:
      'lg:!text-48 lg:!leading-58 font-bold font-centuryGothic text-gray-900 pb-5 text-40 leading-48',
    labelClass:
      'break-words !text-[24px] !leading-[29px] lg:!text-26 lg:!leading-[32px] pb-5 font-bold font-centuryGothic text-gray-900',
    supportingTextClass:
      'break-words font-nunitoSans font-[500] text-gray-900 text-16 leading-[25px] mt-auto',
    imageWrapper: 'flex w-full justify-center pb-12',
  },
});
